import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import {
  clearSelectedOrder,
  getMeeshoReturnById,
} from "app/redux/slice/meeshoSlice";
import Loader from "@jumbo/components/Loader";

const OrderDetailDialog = ({ open, onClose, orderId }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState({});

  useEffect(() => {
    if (open && orderId) {
      setLoading(true);
      dispatch(getMeeshoReturnById({ id: orderId }))
        .unwrap()
        .then((response) => {
          setLoading(false);
          if (response.status !== 200) {
            enqueueSnackbar("Failed to fetch order details", {
              variant: "error",
            });
          }
          setOrderDetail(response?.data?.data?.orderDetail || {});
        })
        .catch((error) => {
          setLoading(false);
          enqueueSnackbar("Failed to fetch order details", {
            variant: "error",
          });
        });
    }
  }, [open, orderId, dispatch, enqueueSnackbar]);

  const handleClose = () => {
    dispatch(clearSelectedOrder());
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Delivered":
        return "success";
      case "Shipped":
        return "info";
      case "Processing":
        return "warning";
      case "Pending":
        return "default";
      case "Cancelled":
        return "error";
      case "Returned":
        return "secondary";
      case "RTO":
        return "error";
      case "Lost":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `₹${amount?.toLocaleString() || 0}`;
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <Loader />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Icon
            icon="material-symbols:receipt-long"
            style={{ fontSize: "24px", color: "#2874f0" }}
          />
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Order Details - {orderDetail?.subOrderNo}
          </Typography>
          <Chip
            label={orderDetail?.orderState}
            color={getStatusColor(orderDetail?.orderState)}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {orderDetail ? (
          <Grid container spacing={3}>
            {/* Order Information */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: "#2874f0" }}>
                    <Icon
                      icon="material-symbols:shopping-cart"
                      style={{ marginRight: "8px" }}
                    />
                    Order Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <Typography variant="body2" color="text.secondary">
                        Sub Order No:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {orderDetail.subOrderNo}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Quantity:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {orderDetail.quantity}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2" color="text.secondary">
                        Order Date:
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(orderDetail.orderDate)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Size:
                      </Typography>
                      <Typography variant="body1">
                        {orderDetail.size}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Product Information */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: "#2874f0" }}>
                    <Icon
                      icon="material-symbols:inventory"
                      style={{ marginRight: "8px" }}
                    />
                    Product Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Product:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {orderDetail.productName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        SKU:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {orderDetail.sku}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Financial Information */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: "#2874f0" }}>
                    <Icon
                      icon="material-symbols:payments"
                      style={{ marginRight: "8px" }}
                    />
                    Financial Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Listed Price:
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(orderDetail.listedPrice)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Discounted Price:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(orderDetail.discountedPrice)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        State:
                      </Typography>
                      <Typography variant="body1">
                        {orderDetail.customerState}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No order details available
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailDialog;
